import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { OpportunityService } from '@app/core';
import { SourTrackEnvelopeOutcome, classifySyncEnvelope } from '../components/sourtrack-sync/sourtrack-sync.utils';

export interface SourTrackFlowOutcome extends SourTrackEnvelopeOutcome {
  /** True when the user's pick was approved, so the quotation list is stale. */
  approved?: boolean;
}

/**
 * Manual SourTrack sync and "approve, then read what was sent". The API interceptor turns any
 * envelope with a 4xx/5xx `statusCode` into an HttpErrorResponse (body in `error`), so a Warning
 * arrives as a value and an Error arrives as a failure; both end up as one outcome here.
 */
@Injectable({ providedIn: 'root' })
export class SourTrackSyncFlowService {
  constructor(private opportunityService: OpportunityService) {}

  sync(leadId: number): Observable<SourTrackFlowOutcome> {
    return this.opportunityService.syncSourTrack(leadId).pipe(
      map((body) => classifySyncEnvelope(body)),
      catchError((error) => of(classifySyncEnvelope(failureBody(error))))
    );
  }

  /**
   * Approves the quotation as a sale order (Pedido de venta). On an awarded opportunity the backend
   * sends it to SourTrack in the same request, so this never calls the sync itself: a second call would
   * open a second attempt for one gesture. It re-reads the opportunity and reports the marker it finds.
   */
  approveAndSync(quotationId: number, leadId: number, adopted: boolean): Observable<SourTrackFlowOutcome> {
    // Sale lines exist only once a quotation is adopted; approving one that is not would send nothing.
    // A never-adopted quotation has no linked sale lines, so adopting with `null` commits directly.
    const adoptFirst: Observable<unknown> = adopted
      ? of(null)
      : this.opportunityService.adoptSaleOrder(quotationId, leadId, null).pipe(
          map((response: any) => {
            if (response?.data?.requiresConfirmation === true) {
              throw { error: { message: 'Adopte el presupuesto desde la ficha antes de aprobarlo.' } };
            }
            return response;
          })
        );
    return adoptFirst.pipe(
      switchMap(() => this.opportunityService.confirmSaleOrder(quotationId, leadId)),
      switchMap(() => this.readMarker(leadId)),
      map((lead) => ({ ...outcomeFromMarker(lead), approved: true })),
      catchError((error) =>
        of<SourTrackFlowOutcome>(
          classifySyncEnvelope({
            statusCode: 500,
            messageType: 'Error',
            message: error?.error?.message || 'No se pudo adoptar y aprobar el presupuesto.',
          })
        )
      )
    );
  }

  /** The opportunity's current sync marker, or null when it cannot be read. */
  readMarker(leadId: number): Observable<any | null> {
    return this.opportunityService.getOpportunityById(leadId).pipe(
      map((response: any) => {
        const rows = response?.data?.rows;
        return Array.isArray(rows) && rows.length > 0 ? rows[0] : null;
      }),
      catchError(() => of(null))
    );
  }
}

function outcomeFromMarker(lead: any | null): SourTrackFlowOutcome {
  const detail = lead?.sourTrackSyncStatusDescription;
  switch (lead?.sourTrackSyncStatus) {
    case 'Integrated':
      return { severity: 'success', message: 'Presupuesto aprobado y enviado a SourTrack.', lead };
    case 'Failed':
      return {
        severity: 'error',
        message: 'Presupuesto aprobado, pero el envío a SourTrack falló.' + (detail ? ` ${detail}` : ''),
        lead,
      };
    case 'Pending':
      return {
        severity: 'warning',
        message: 'Presupuesto aprobado, pero no se envió a SourTrack.' + (detail ? ` ${detail}` : ''),
        lead,
      };
    default:
      return { severity: 'warning', message: 'Presupuesto aprobado como pedido de venta.', lead };
  }
}

function failureBody(error: any): any {
  return error?.error && typeof error.error === 'object'
    ? { statusCode: error.status, messageType: 'Error', ...error.error }
    : { statusCode: error?.status || 500, messageType: 'Error' };
}
